<template>
  <NuxtImg v-bind="imgAttrs" />
</template>

<script setup lang="ts">
interface PreloadOptions {
  fetchPriority?: 'auto' | 'high' | 'low'
}

const { preload = false } = defineProps<{
  preload?: boolean | PreloadOptions
}>()

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const $img = useImage()

const shouldPreload = computed(() => Boolean(preload))

const fetchPriority = computed(() => {
  if (typeof preload === 'object' && preload.fetchPriority) {
    return preload.fetchPriority
  }
  return undefined
})

const hasWidthBasedSizes = computed(() => Boolean(attrs.sizes))

const imgAttrs = computed(() => ({
  ...attrs,
  preload: shouldPreload.value && !hasWidthBasedSizes.value ? preload : false,
}))

const responsivePreload = computed(() => {
  const src = attrs.src
  if (!shouldPreload.value || !hasWidthBasedSizes.value || !src) {
    return null
  }

  return $img.getSizes(String(src), {
    provider: attrs.provider as string | undefined,
    sizes: attrs.sizes as string | undefined,
    densities: attrs.densities as string | undefined,
    modifiers: attrs.modifiers as Record<string, unknown> | undefined,
  })
})

// NuxtImg preload only emits imagesrcset for density (1x/2x) srcsets, not width
// descriptors — inject a responsive preload link when sizes is set.
useHead(
  computed(() => {
    const responsive = responsivePreload.value
    if (!responsive?.srcset) {
      return {}
    }

    return {
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: responsive.src,
          imagesrcset: responsive.srcset,
          imagesizes: responsive.sizes,
          ...(fetchPriority.value && { fetchpriority: fetchPriority.value }),
        },
      ],
    }
  })
)
</script>
